import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { forestArrow, forestGroup, PageHeader } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { rangeCompact, rangeSentence } from './calendarLabels';
import { CalendarRangeTools } from './CalendarRangeTools';
import type { CalendarDensity, CalendarMode } from './calendarSearch';
import type { CalendarView } from './useCalendarView';

export const MODE_LABEL: Record<CalendarMode, string> = {
  apartments: 'Apartments',
  parking: 'Stellplätze',
  resources: 'Ressourcen',
};

interface Props {
  view: CalendarView;
  today: string;
  /** Die Modi, die diese Rolle und dieses Objekt haben; einer allein braucht keine Reiter. */
  modes: CalendarMode[];
  onModeChange: (mode: CalendarMode) => void;
  onOpenFilters: () => void;
}

/**
 * Wald-Kopf der Belegung (Board „Belegung“): Pfad und Bereichsumschalter aus dem Gerüst, Titel mit
 * Zeitraumsatz, Zeitraum-Werkzeuge und die Modi als Reiter. Am Handy stehen die Pfeile neben dem
 * Titel, darunter Dichte und „Filter und Ansicht“ – Modi, Heute und Zeitraum liegen im Blatt.
 */
export function CalendarPageHeader({ view, today, modes, onModeChange, onOpenFilters }: Props) {
  if (view.mobile) {
    return (
      <PageHeader
        className="pb-[70px]"
        title="Belegung"
        description={rangeCompact(view.from, view.to)}
        tools={
          <div className={cn(forestGroup, 'h-11')}>
            <button
              type="button"
              onClick={view.previous}
              aria-label="Vorheriger Zeitraum"
              className={forestArrow}
            >
              <ChevronLeft size={18} aria-hidden />
            </button>
            <button
              type="button"
              onClick={view.next}
              aria-label="Nächster Zeitraum"
              className={forestArrow}
            >
              <ChevronRight size={18} aria-hidden />
            </button>
          </div>
        }
        below={
          <div className="flex items-center gap-2">
            <DensityGroup value={view.custom ? null : view.density} onChange={view.setDensity} />
            <button
              type="button"
              onClick={onOpenFilters}
              className="rounded-pill text-forest-deep flex h-11 min-w-0 grow items-center justify-center gap-1.5 bg-white px-3 text-[13px] font-bold"
            >
              <SlidersHorizontal size={14} aria-hidden />
              Filter und Ansicht
            </button>
          </div>
        }
      />
    );
  }
  return (
    <PageHeader
      className="pb-[84px] md:pb-[84px]"
      title="Belegung"
      description={rangeSentence(view.from, view.to, today)}
      tools={
        <CalendarRangeTools
          from={view.from}
          to={view.to}
          zoom={view.zoom}
          custom={view.custom}
          onRangeChange={view.showRange}
          onZoomChange={view.setZoom}
          onPrevious={view.previous}
          onNext={view.next}
          onToday={() => view.goToday(today)}
        />
      }
      filterTabs={
        modes.length > 1
          ? modes.map((mode) => ({
              label: MODE_LABEL[mode],
              active: view.mode === mode,
              onSelect: () => onModeChange(mode),
            }))
          : undefined
      }
      filterTabsKind="tabs"
      filterTabsLabel="Was zeigen"
    />
  );
}

function DensityGroup({
  value,
  onChange,
}: {
  value: CalendarDensity | null;
  onChange: (value: CalendarDensity) => void;
}) {
  return (
    <div role="radiogroup" aria-label="Tage pro Ansicht" className={cn(forestGroup, 'flex-none')}>
      {([3, 7, 14] as const).map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          onClick={() => onChange(option)}
          className={cn(
            'rounded-pill h-11 min-w-11 px-2.5 font-mono text-xs font-semibold',
            value === option ? 'text-forest-deep bg-white' : 'text-white',
          )}
        >
          {option} T
        </button>
      ))}
    </div>
  );
}
